import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { Home, ArrowLeft, CalendarDays, Clock, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30"
];

const serviceTypes = [
  { value: "first_time_buyer", label: "First Time Buyer" },
  { value: "remortgage", label: "Remortgage" },
  { value: "buy_to_let", label: "Buy to Let" },
  { value: "moving_home", label: "Moving Home" },
  { value: "commercial", label: "Commercial Mortgage" },
  { value: "protection", label: "Protection Review" },
  { value: "general", label: "General Enquiry" }
];

export default function AppointmentBooking() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    time: "",
    service_type: "",
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Please select a date");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...formData,
        date: format(date, "yyyy-MM-dd")
      });
      setBookingComplete(true);
      toast.success("Appointment booked successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book appointment");
    }
    setLoading(false);
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Booking Confirmed!</h2>
          <p className="text-slate-400 mb-2">
            Your appointment is scheduled for:
          </p>
          <p className="text-xl font-semibold text-blue-500 mb-1">
            {format(date, "EEEE, MMMM d, yyyy")}
          </p>
          <p className="text-lg text-slate-300 mb-6">at {formData.time}</p>
          <p className="text-slate-400 mb-8">
            We've sent a confirmation email to {formData.client_email}
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/">
              <Button variant="outline" className="border-slate-700">
                Back to Home
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Create Account
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-amber-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <CalendarDays className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Book Your Consultation</h1>
            <p className="text-slate-400">Schedule a free consultation with our expert mortgage advisors</p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400'
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-16 h-0.5 ${step > s ? 'bg-blue-600' : 'bg-slate-800'}`}></div>}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Card className="bg-[#0f172a] border-slate-800">
                  <CardHeader>
                    <CardTitle>Select Date & Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <Label className="mb-3 block">Select a date</Label>
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date() || date.getDay() === 0}
                          className="rounded-md border border-slate-700 bg-[#020617]"
                          data-testid="appointment-calendar"
                        />
                      </div>
                      <div>
                        <Label className="mb-3 block">Select a time</Label>
                        <div className="grid grid-cols-4 gap-2">
                          {timeSlots.map((time) => (
                            <Button
                              key={time}
                              type="button"
                              variant={formData.time === time ? "default" : "outline"}
                              className={`${formData.time === time ? 'bg-blue-600' : 'border-slate-700 hover:bg-slate-800'}`}
                              onClick={() => setFormData({ ...formData, time })}
                              data-testid={`time-slot-${time}`}
                            >
                              {time}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                      <Button
                        type="button"
                        onClick={() => setStep(2)}
                        disabled={!date || !formData.time}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="next-step-1-btn"
                      >
                        Continue
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Card className="bg-[#0f172a] border-slate-800">
                  <CardHeader>
                    <CardTitle>Your Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          placeholder="John Doe"
                          value={formData.client_name}
                          onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                          required
                          className="bg-[#020617] border-slate-700"
                          data-testid="appointment-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          value={formData.client_email}
                          onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                          required
                          className="bg-[#020617] border-slate-700"
                          data-testid="appointment-email-input"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+44 7123 456789"
                          value={formData.client_phone}
                          onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                          required
                          className="bg-[#020617] border-slate-700"
                          data-testid="appointment-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="service">Service Type</Label>
                        <Select
                          value={formData.service_type}
                          onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                        >
                          <SelectTrigger className="bg-[#020617] border-slate-700" data-testid="appointment-service-select">
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f172a] border-slate-700">
                            {serviceTypes.map((service) => (
                              <SelectItem key={service.value} value={service.value}>
                                {service.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex justify-between mt-6">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setStep(1)}
                        className="border-slate-700"
                      >
                        Back
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setStep(3)}
                        disabled={!formData.client_name || !formData.client_email || !formData.client_phone || !formData.service_type}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="next-step-2-btn"
                      >
                        Continue
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Card className="bg-[#0f172a] border-slate-800">
                  <CardHeader>
                    <CardTitle>Confirm Booking</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-[#020617] rounded-lg p-6 mb-6">
                      <h3 className="font-semibold mb-4">Booking Summary</h3>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-400">Date</p>
                          <p className="font-medium">{date && format(date, "EEEE, MMMM d, yyyy")}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Time</p>
                          <p className="font-medium">{formData.time}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Name</p>
                          <p className="font-medium">{formData.client_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Email</p>
                          <p className="font-medium">{formData.client_email}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Phone</p>
                          <p className="font-medium">{formData.client_phone}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Service</p>
                          <p className="font-medium">
                            {serviceTypes.find(s => s.value === formData.service_type)?.label}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 mb-6">
                      <Label htmlFor="notes">Additional Notes (optional)</Label>
                      <Textarea
                        id="notes"
                        placeholder="Any specific questions or information you'd like to share..."
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="bg-[#020617] border-slate-700"
                        rows={3}
                        data-testid="appointment-notes-input"
                      />
                    </div>
                    <div className="flex justify-between">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setStep(2)}
                        className="border-slate-700"
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="confirm-booking-btn"
                      >
                        {loading ? "Booking..." : "Confirm Booking"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>
    </div>
  );
}
